import { useState } from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Loader2 } from "lucide-react";
import CustomPagination from "./CustomPagination";
interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  handlePageChange?: (page: number) => void;
  meta?: object;
  isLoading: boolean;
  showPagination?: boolean;
}

const CustomTable = <TData, TValue>({
  columns,
  data,
  meta,
  isLoading,
  handlePageChange,
  showPagination,
}: DataTableProps<TData, TValue>) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    // pageCount: meta?.pageCount,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    manualPagination: true,

  });

  // When using grouped columns, `columns.length` is not the same as the number of rendered table columns.
  // Use visible leaf columns for accurate colSpan so loaders/renderers stay centered.
  const visibleLeafColumnCount = table.getVisibleLeafColumns().length;
  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader className="bg-off-white-1">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan > 1 ? header.colSpan : undefined}
                    rowSpan={header.rowSpan > 1 ? header.rowSpan : undefined}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {!isLoading ? (
            <>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={visibleLeafColumnCount}
                    className="h-24 p-0"
                  >
                    <div className="flex items-center justify-center w-full h-full text-gray-80">
                      No results.
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </>
          ) : (
            <TableRow>
              <TableCell colSpan={visibleLeafColumnCount} className="h-24 p-0 ">
                <div className="flex items-center justify-center w-full h-full">
                  <Loader2 className="animate-spin " />
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {showPagination && (
        <>
          {isLoading || table.getRowModel().rows?.length <= 0 ? null : (
            <CustomPagination
              meta={meta as any}
              table={table}
              currentPage={(meta as any)?.page}
              totalPages={(meta as any)?.pageCount}
              onPageChange={handlePageChange as any}
              showPreviousNext
            />
          )}
        </>
      )}
    </div>
  );
};

export default CustomTable;

{
  /* {isLoading ||
table.getRowModel().rows?.length <=0
? null:
      <CustomPagination
      meta={meta}
      table={table}
      currentPage={meta?.page}
      totalPages={meta?.pageCount}
      onPageChange={handlePageChange}
      showPreviousNext
      />
      </div>
    } */
}
